import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import api from '../utils/api';
import { toast } from 'react-toastify';
import WardPhotoSubmit from '../components/WardPhotoSubmit';

const WardPage = () => {
  const { wardNumber: rawWardNumber } = useParams();
  const navigate = useNavigate();
  const wardNumber = rawWardNumber?.startsWith('ward-') ? rawWardNumber.slice(5) : rawWardNumber;

  const [wardData, setWardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [wardAd, setWardAd] = useState(null);
  const [defaultBanner, setDefaultBanner] = useState(null);
  const [signupMode, setSignupMode] = useState('email'); // 'email' or 'site'

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    postal_code: '',
  });

  const fetchWardData = useCallback(async () => {
    try {
      const response = await api.get(`/wards/${wardNumber}`);
      setWardData(response.data);
    } catch (error) {
      console.error('Error fetching ward data:', error);
    } finally {
      setLoading(false);
    }
  }, [wardNumber]);

  const fetchWardAd = useCallback(async () => {
    try {
      const response = await api.get(`/ward-realtor-ads/${wardNumber}`);
      setWardAd(response.data);
    } catch (error) {
      console.error('Error fetching ward ad:', error);
    }
  }, [wardNumber]);

  const fetchDefaultBanner = useCallback(async () => {
    try {
      const response = await api.get('/default-ward-banner');
      setDefaultBanner(response.data);
    } catch (error) {
      console.error('Error fetching default banner:', error);
    }
  }, []);

  useEffect(() => {
    fetchWardData();
    fetchWardAd();
    fetchDefaultBanner();
  }, [fetchWardData, fetchWardAd, fetchDefaultBanner]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (signupMode === 'site') {
        if (formData.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          setSubmitting(false);
          return;
        }
        // Full site registration
        await api.post('/auth/register', {
          name: formData.name,
          email: formData.email,
          username: formData.username,
          password: formData.password,
          postal_code: formData.postal_code,
        });
        // Also subscribe to ward emails
        await api.post('/wards/subscribe', {
          name: formData.name,
          email: formData.email,
          ward: `Ward ${wardNumber}`,
          councillor_id: wardData?.councillor?.id || null,
          opted_in_councillor_emails: true,
          opted_in_cyqr_emails: true,
        });
        toast.success('Account created! Check your email to verify.');
      } else {
        // Email list only
        await api.post('/wards/subscribe', {
          name: formData.name,
          email: formData.email,
          ward: `Ward ${wardNumber}`,
          councillor_id: wardData?.councillor?.id || null,
          opted_in_councillor_emails: true,
          opted_in_cyqr_emails: true,
        });
        toast.success('Subscribed! Check your email to verify.');
      }
      setFormData({ name: '', email: '', username: '', password: '', postal_code: '' });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInput = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#131313] flex items-center justify-center">
          <div className="text-[#E5E2E1] font-['Manrope'] tracking-widest text-xs uppercase">Loading ward data...</div>
        </div>
      </Layout>
    );
  }

  if (!wardData) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#131313] flex items-center justify-center">
          <div className="text-red-400 font-['Manrope'] tracking-widest text-xs uppercase">Ward not found</div>
        </div>
      </Layout>
    );
  }

  const councillor = wardData.councillor;
  const content = wardData.content;
  const profile = wardData.profile;
  const councillorBio = content?.councillor_bio_override || councillor?.bio || '';
  const subscriberCount = wardData.subscriber_count || 0;
  const wardLabel = wardNumber === '11' ? 'REGINA' : `WARD ${wardNumber}`;
  const wardLabelLower = wardNumber === '11' ? 'Regina' : `Ward ${wardNumber}`;

  return (
    <Layout>
      <div className="min-h-screen bg-[#131313] text-[#E5E2E1] font-['Manrope']">

        {/* ===== HERO: Councillor Profile ===== */}
        <section className="relative min-h-[600px] lg:min-h-[750px] flex items-center pt-12 lg:pt-20 overflow-hidden bg-[#131313]" data-testid="ward-hero-section">
          {/* Background accent shape */}
          <div className="absolute top-0 right-0 w-1/2 h-full bg-[#1C1B1B] -skew-x-12 translate-x-1/4 z-0 hidden lg:block" />

          <div className="w-full max-w-7xl mx-auto px-6 lg:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left: Text Content */}
            <div className="lg:col-span-7 space-y-6 lg:space-y-8">
              <div className="inline-block px-4 py-1 bg-[#00FF41]/10 border-l-4 border-[#00e639]">
                <span className="text-[#00e639] text-[10px] font-extrabold tracking-[0.2em] uppercase" data-testid="ward-representative-label">
                  {wardLabel} REPRESENTATIVE
                </span>
              </div>

              <h1 className="text-5xl sm:text-7xl lg:text-8xl font-extrabold tracking-tighter leading-[0.9]" data-testid="councillor-name-heading">
                {councillor?.name ? (
                  <>
                    {councillor.name.split(' ').slice(0, -1).join(' ')}<br />
                    <span className="text-[#FFD700]">{councillor.name.split(' ').slice(-1)[0]?.toUpperCase()}</span>
                  </>
                ) : (
                  <span className="text-[#FFD700]">{wardLabel}</span>
                )}
              </h1>

              <div className="max-w-xl">
                <p className="text-base lg:text-lg text-[#d0c6ab] font-light leading-relaxed mb-6 whitespace-pre-line">
                  {councillorBio || `Dedicated to serving the residents of ${wardLabelLower} with a focus on sustainable infrastructure, community safety, and transparent governance.`}
                </p>

                {/* Contact Grid */}
                <div className="grid grid-cols-2 gap-6 p-5 bg-[#2A2A2A] border-l-2 border-[#FFD700]/30">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#999077] mb-1">Direct Contact</p>
                    <p className="text-[#FFD700] font-bold text-sm" data-testid="councillor-phone">
                      {councillor?.phone || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#999077] mb-1">Email</p>
                    <button
                      onClick={() => navigate('/relay')}
                      className="text-[#E5E2E1] font-medium text-sm hover:text-[#FFD700] transition-colors"
                      data-testid="email-anonymously-btn"
                    >
                      Anonymously
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Portrait */}
            <div className="lg:col-span-5 relative group" data-testid="councillor-photo-container">
              <div className="absolute -inset-4 border border-[#FFD700]/20 -z-10 translate-x-4 translate-y-4 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform hidden lg:block" />
              {councillor?.image_url ? (
                <img
                  alt={councillor.name || 'Councillor'}
                  className="w-full grayscale hover:grayscale-0 transition-all duration-700 object-cover aspect-[4/5] shadow-2xl"
                  src={councillor.image_url}
                  data-testid="councillor-photo"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(councillor.name || 'Ward')}&background=1C1B1B&color=E5E2E1&size=500`;
                  }}
                />
              ) : (
                <div className="w-full aspect-[4/5] bg-[#1C1B1B] flex items-center justify-center">
                  <span className="text-6xl font-extrabold text-[#2A2A2A]">{wardNumber}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ===== STAY CONNECTED / SIGNUP ===== */}
        <section className="bg-[#1C1B1B] py-16 lg:py-24 relative overflow-hidden" data-testid="stay-connected-section">
          {/* Background watermark */}
          <div className="absolute top-0 right-0 p-16 lg:p-24 opacity-[0.03] pointer-events-none select-none">
            <span className="text-[150px] lg:text-[200px] font-extrabold leading-none text-[#E5E2E1] tracking-tighter">
              {wardNumber.toString().padStart(2, '0')}
            </span>
          </div>

          <div className="max-w-4xl mx-auto px-6 relative z-10">
            <div className="text-center space-y-4 mb-10">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter uppercase" data-testid="stay-connected-heading">
                STAY CONNECTED WITH {wardLabel}
              </h2>
              <p className="text-[#d0c6ab] max-w-2xl mx-auto text-sm lg:text-base">
                Get updates, community event notifications, and direct correspondence from the councillor to your inbox.
              </p>
            </div>

            {/* Toggle: Email List vs Site Account */}
            <div className="flex justify-center mb-8" data-testid="signup-mode-toggle">
              <div className="inline-flex bg-[#0E0E0E] p-1">
                <button
                  type="button"
                  onClick={() => setSignupMode('email')}
                  className={`px-6 py-2.5 text-[11px] font-extrabold tracking-widest uppercase transition-all ${
                    signupMode === 'email'
                      ? 'bg-[#00FF41] text-[#003907]'
                      : 'text-[#999077] hover:text-[#E5E2E1]'
                  }`}
                  data-testid="toggle-email-list"
                >
                  Email List
                </button>
                <button
                  type="button"
                  onClick={() => setSignupMode('site')}
                  className={`px-6 py-2.5 text-[11px] font-extrabold tracking-widest uppercase transition-all ${
                    signupMode === 'site'
                      ? 'bg-[#FFD700] text-[#3a3000]'
                      : 'text-[#999077] hover:text-[#E5E2E1]'
                  }`}
                  data-testid="toggle-site-signup"
                >
                  Create Account
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="max-w-lg mx-auto" data-testid="signup-form">
              {signupMode === 'email' ? (
                /* Email List: Name + Email inline */
                <div className="flex flex-col md:flex-row items-stretch gap-3">
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInput('name', e.target.value)}
                    className="flex-1 bg-[#353534] border-none text-[#E5E2E1] px-4 py-4 text-xs font-bold tracking-widest placeholder:text-[#999077] focus:ring-2 focus:ring-[#00e639] focus:ring-offset-2 focus:ring-offset-[#1C1B1B] outline-none transition-all"
                    placeholder="YOUR NAME"
                    data-testid="input-name"
                  />
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInput('email', e.target.value)}
                    className="flex-1 bg-[#353534] border-none text-[#E5E2E1] px-4 py-4 text-xs font-bold tracking-widest placeholder:text-[#999077] focus:ring-2 focus:ring-[#00e639] focus:ring-offset-2 focus:ring-offset-[#1C1B1B] outline-none transition-all"
                    placeholder="EMAIL ADDRESS"
                    data-testid="input-email"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-8 py-4 bg-[#00FF41] text-[#003907] font-extrabold tracking-tighter uppercase text-sm hover:shadow-[0_0_25px_rgba(0,255,65,0.4)] active:scale-95 transition-all disabled:opacity-50 whitespace-nowrap"
                    data-testid="subscribe-btn"
                  >
                    {submitting ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
              ) : (
                /* Site Signup: Full fields */
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => handleInput('name', e.target.value)}
                      className="w-full bg-[#353534] border-none text-[#E5E2E1] px-4 py-4 text-xs font-bold tracking-widest placeholder:text-[#999077] focus:ring-2 focus:ring-[#FFD700] focus:ring-offset-2 focus:ring-offset-[#1C1B1B] outline-none transition-all"
                      placeholder="YOUR NAME"
                      data-testid="input-name-site"
                    />
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInput('email', e.target.value)}
                      className="w-full bg-[#353534] border-none text-[#E5E2E1] px-4 py-4 text-xs font-bold tracking-widest placeholder:text-[#999077] focus:ring-2 focus:ring-[#FFD700] focus:ring-offset-2 focus:ring-offset-[#1C1B1B] outline-none transition-all"
                      placeholder="EMAIL ADDRESS"
                      data-testid="input-email-site"
                    />
                  </div>
                  <input
                    type="text"
                    required
                    value={formData.username}
                    onChange={(e) => handleInput('username', e.target.value)}
                    className="w-full bg-[#353534] border-none text-[#E5E2E1] px-4 py-4 text-xs font-bold tracking-widest placeholder:text-[#999077] focus:ring-2 focus:ring-[#FFD700] focus:ring-offset-2 focus:ring-offset-[#1C1B1B] outline-none transition-all"
                    placeholder="CHOOSE A USERNAME"
                    data-testid="input-username"
                  />
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => handleInput('password', e.target.value)}
                    className="w-full bg-[#353534] border-none text-[#E5E2E1] px-4 py-4 text-xs font-bold tracking-widest placeholder:text-[#999077] focus:ring-2 focus:ring-[#FFD700] focus:ring-offset-2 focus:ring-offset-[#1C1B1B] outline-none transition-all"
                    placeholder="PASSWORD (MIN 8 CHARACTERS)"
                    data-testid="input-password"
                  />
                  <input
                    type="text"
                    required
                    value={formData.postal_code}
                    onChange={(e) => handleInput('postal_code', e.target.value)}
                    className="w-full bg-[#353534] border-none text-[#E5E2E1] px-4 py-4 text-xs font-bold tracking-widest placeholder:text-[#999077] focus:ring-2 focus:ring-[#FFD700] focus:ring-offset-2 focus:ring-offset-[#1C1B1B] outline-none transition-all"
                    placeholder="POSTAL CODE"
                    data-testid="input-postal-code"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 bg-gradient-to-r from-[#FFD700] to-[#705E00] text-[#3a3000] font-extrabold tracking-tighter uppercase text-sm hover:shadow-[0_0_15px_rgba(255,215,0,0.3)] active:scale-95 transition-all disabled:opacity-50"
                    data-testid="create-account-btn"
                  >
                    {submitting ? 'Creating Account...' : 'Create Account & Subscribe'}
                  </button>
                </div>
              )}
            </form>

            {/* Subscriber count */}
            {subscriberCount > 0 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <div className="flex -space-x-2">
                  {[1,2,3].map(i => (
                    <div key={`avatar-${i}`} className="w-7 h-7 rounded-full border-2 border-[#131313] bg-[#2A2A2A] overflow-hidden" />
                  ))}
                </div>
                <p className="text-[10px] text-[#999077] uppercase tracking-widest font-bold">
                  <span className="text-[#00e639]">{subscriberCount.toLocaleString()}+</span> residents already subscribed
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ===== ABOUT WARD ===== */}
        <section className="py-20 lg:py-32 bg-[#131313]" data-testid="about-ward-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Header row */}
            <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div className="max-w-2xl">
                <span className="text-[#FFD700] text-[10px] font-extrabold tracking-[0.3em] uppercase block mb-3">THE HEART OF REGINA</span>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter uppercase leading-[0.9]" data-testid="about-ward-heading">
                  ABOUT {wardLabel}
                </h2>
              </div>
              <div className="h-[1px] flex-grow bg-[#4D4732]/30 mb-3 hidden md:block" />
              <p className="text-[#999077] text-[10px] font-medium uppercase tracking-widest mb-3 whitespace-nowrap">EST. 1903 / DISTRICT ALPHA</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              {/* Text */}
              <div className="md:col-span-5 space-y-10">
                <div className="space-y-5">
                  <p className="text-lg lg:text-xl text-[#E5E2E1] leading-relaxed font-light whitespace-pre-line">
                    {content?.about_ward || `${wardLabelLower} represents a unique fusion of historical charm and modern development. Encompassing a vital portion of our city, it serves as the cultural and administrative anchor of Regina.`}
                  </p>
                  <p className="text-[#d0c6ab] font-light text-sm leading-relaxed">
                    From the prestigious streets of <span className="text-[#E5E2E1] font-semibold">Lakeview</span> to the vibrant energy of the <span className="text-[#E5E2E1] font-semibold">Albert Park</span> commercial corridor, this ward is home to diverse families, thriving businesses, and some of our province's most iconic landmarks.
                  </p>
                </div>
                <div className="space-y-3">
                  <h4 className="text-[10px] font-extrabold tracking-widest text-[#FFD700] uppercase">Key Landmarks</h4>
                  <ul className="space-y-3">
                    {['Wascana Centre & Legislative Assembly', 'The Royal Saskatchewan Museum', 'McKell Wascana Conservation Park'].map((item) => (
                      <li key={item} className="flex items-center gap-4 text-sm uppercase tracking-tight font-bold text-[#E5E2E1] group">
                        <span className="w-2 h-2 bg-[#00e639] group-hover:scale-150 transition-transform flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Visual Grid */}
              <div className="md:col-span-7 grid grid-cols-2 gap-4 min-h-[400px]">
                <div
                  className="bg-[#2A2A2A] relative overflow-hidden group flex items-end p-6"
                  data-testid="ward-bento-primary"
                >
                  {content?.bento_primary_image_url && (
                    <img
                      src={content.bento_primary_image_url}
                      alt="Urban Living"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E0E] to-transparent opacity-80" />
                  <div className="relative flex items-center justify-between w-full">
                    <p className="text-[10px] font-extrabold tracking-widest uppercase">Urban Living</p>
                    <WardPhotoSubmit ward={wardNumber} slot="primary" />
                  </div>
                </div>
                <div className="grid grid-rows-2 gap-4">
                  <div
                    className="bg-[#2A2A2A] relative overflow-hidden group flex items-end p-4"
                    data-testid="ward-bento-secondary"
                  >
                    {content?.bento_secondary_image_url && (
                      <img
                        src={content.bento_secondary_image_url}
                        alt="Green Spaces"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E0E] to-transparent opacity-80" />
                    <div className="relative flex items-center justify-between w-full">
                      <p className="text-[10px] font-extrabold tracking-widest uppercase">Green Spaces</p>
                      <WardPhotoSubmit ward={wardNumber} slot="secondary" />
                    </div>
                  </div>
                  <div className="relative overflow-hidden" data-testid="ward-bento-map">
                    {content?.bento_map_image_url && (
                      <img
                        src={content.bento_map_image_url}
                        alt="Ward map"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    )}
                    <div className={`absolute inset-0 ${content?.bento_map_image_url ? 'bg-[#FFD700]/85' : 'bg-[#FFD700]'}`} />
                    <div className="relative p-6 flex flex-col justify-between h-full">
                      <span className="material-symbols-outlined text-[#705E00] text-4xl">location_on</span>
                      <h5 className="text-[#705E00] text-xl lg:text-2xl font-extrabold tracking-tighter leading-none uppercase">Regina's Civic Core</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== REALTOR AD (only if exists in DB) ===== */}
        {wardAd && (
          <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-20 lg:mb-32" data-testid="realtor-ad-section">
            <div className="bg-[#0E0E0E] border border-[#4D4732]/20 p-1 lg:p-1">
              <div className="flex flex-col md:flex-row items-stretch">
                {/* Image */}
                <div className="md:w-1/3 relative min-h-[250px]">
                  {wardAd.image_url && (
                    <img
                      className="absolute inset-0 w-full h-full object-cover transition-all duration-700"
                      src={wardAd.image_url}
                      alt={wardAd.business_name}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0E0E0E] hidden md:block" />
                </div>
                {/* Content */}
                <div className="md:w-2/3 p-8 lg:p-12 flex flex-col justify-center space-y-5">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                    <div>
                      <span className="text-[#00e639] text-[10px] font-extrabold tracking-[0.4em] uppercase mb-2 block italic">Exclusive Partnership</span>
                      <h3 className="text-3xl lg:text-4xl font-extrabold tracking-tighter uppercase leading-none" data-testid="ad-business-name">
                        {wardAd.business_name}
                      </h3>
                      {wardAd.expert_name && (
                        <p className="text-[#999077] text-xs font-bold tracking-widest uppercase mt-2">
                          FEATURED EXPERT: <span className="text-[#FFD700]">{wardAd.expert_name}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  {wardAd.description && (
                    <p className="text-[#d0c6ab] font-light text-sm max-w-xl leading-relaxed whitespace-pre-line">
                      {wardAd.description}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-6 pt-2">
                    {wardAd.cta_visible && wardAd.cta_url && (
                      <a
                        href={wardAd.cta_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-8 py-3 bg-[#E5E2E1] text-[#0E0E0E] text-[10px] font-extrabold tracking-widest uppercase hover:bg-[#FFD700] transition-colors"
                        data-testid="ad-cta-btn"
                      >
                        {wardAd.cta_label || 'VIEW LISTINGS'}
                      </a>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-[#00e639] text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#999077]">Top 1% Regina Realtors</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ===== DEFAULT BANNER (when no realtor ad for this ward) ===== */}
        {!wardAd && defaultBanner && defaultBanner.image_url && (
          <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-20 lg:mb-32" data-testid="default-banner-section">
            <a
              href={defaultBanner.link_url || '/ads'}
              className="block overflow-hidden border border-[#4D4732]/20 hover:border-[#FFD700]/30 transition-colors"
            >
              <img
                src={defaultBanner.image_url}
                alt="Advertise here"
                className="w-full h-auto object-cover"
                data-testid="default-banner-image"
              />
            </a>
          </section>
        )}
      </div>
    </Layout>
  );
};

export default WardPage;
